import {
  ActionIcon,
  Alert,
  Button,
  Group,
  Loader,
  Modal,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { useMutation } from "@tanstack/react-query";
import { useMemo, useState, type ReactElement } from "react";
import { useTranslation } from "react-i18next";

import { useEnabledModules } from "../modules/useEnabledModules";
import { RepoLabelsImport } from "./RepoLabelsImport";
import { useConnectedForgejoRepos } from "./useForgejoRepos";
import { useLabelMappings, useLabelRoles } from "./useLabelMappings";
import { createLabelMapping, deleteLabelMapping } from "./labelMappingApi";

// Секция «Семантика меток» на `/settings/connections`.
//
// Выбор scope (org-дефолт / конкретный repo), таблица текущих
// маппингов scope, форма добавления соответствия «метка → роль» с
// клиентской валидацией role_value, удаление строки с confirm.
//
// Импорт меток репо (`RepoLabelsImport`) показывается, когда выбран
// репозиторий и включён модуль `task_tracker`: секция живёт в разделе
// `repo_host`, а путь каталога меток — у `task_tracker`.
export function LabelMappingSection(): ReactElement {
  const { t } = useTranslation();

  // scope === null — дефолт организации; строка — override конкретного
  // репозитория. Mantine Select отдаёт ровно `string | null`, поэтому
  // храним то же без sentinel-маркера.
  const [repo, setRepo] = useState<string | null>(null);

  const reposQuery = useConnectedForgejoRepos(true);
  const mappingsQuery = useLabelMappings(repo);
  const rolesQuery = useLabelRoles();
  const modules = useEnabledModules();

  const [labelName, setLabelName] = useState<string>("");
  const [role, setRole] = useState<string | null>(null);
  const [roleValue, setRoleValue] = useState<string>("");
  const [formError, setFormError] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<DeleteTarget | null>(null);

  const refetchMappings = (): void => {
    void mappingsQuery.refetch();
  };

  const createMutation = useMutation({
    mutationFn: createLabelMapping,
    onSuccess: () => {
      setLabelName("");
      setRole(null);
      setRoleValue("");
      setFormError(null);
      refetchMappings();
    },
    onError: () => {
      setFormError(t("connections.labelMapping.saveError"));
    },
  });

  const deleteMutation = useMutation({
    mutationFn: deleteLabelMapping,
    onSuccess: () => {
      setDeleteTarget(null);
      refetchMappings();
    },
  });

  const roleOptions = (rolesQuery.data?.roles ?? []).map((info) => ({
    value: info.role,
    label: info.role,
  }));
  const requiresValue =
    role !== null &&
    (rolesQuery.data?.roles.find((info) => info.role === role)
      ?.requires_value ??
      false);
  const rolesUnavailable = rolesQuery.data === undefined;

  // Пробелы по краям метки и значения backend отклоняет (`422`): метка
  // трекера с ними не совпадёт, поэтому форма обрезает их до проверки.
  const handleAdd = (): void => {
    const trimmedLabelName = labelName.trim();
    const trimmedRoleValue = roleValue.trim();
    if (trimmedLabelName === "" || role === null) {
      setFormError(t("connections.labelMapping.formIncomplete"));
      return;
    }
    if (requiresValue && trimmedRoleValue === "") {
      setFormError(t("connections.labelMapping.roleValueRequired"));
      return;
    }
    createMutation.mutate({
      repository_full_name: repo,
      label_name: trimmedLabelName,
      role,
      role_value: requiresValue ? trimmedRoleValue : null,
    });
  };

  const mappedNames = useMemo(
    () =>
      new Set((mappingsQuery.data?.mappings ?? []).map((m) => m.label_name)),
    [mappingsQuery.data],
  );

  const repoOptions = (reposQuery.data?.repos ?? []).map((name) => ({
    value: name,
    label: name,
  }));

  return (
    <>
      <Stack gap="md">
        <Title order={3}>{t("connections.labelMapping.title")}</Title>

        <Select
          label={t("connections.labelMapping.scopeLabel")}
          description={t("connections.labelMapping.scopeHint")}
          data={repoOptions}
          placeholder={t("connections.labelMapping.orgScope")}
          value={repo}
          onChange={setRepo}
          clearable
        />

        {repo !== null && modules.isEnabled("task_tracker") ? (
          <RepoLabelsImport repo={repo} mappedNames={mappedNames} />
        ) : null}

        <Title order={4}>{t("connections.labelMapping.mappingsTitle")}</Title>

        {mappingsQuery.isPending ? (
          <Loader />
        ) : mappingsQuery.isError ? (
          <Alert color="red" title={t("connections.errorHeading")}>
            {t("connections.loadingError")}
          </Alert>
        ) : mappingsQuery.data.mappings.length === 0 ? (
          <Text c="dimmed">{t("connections.labelMapping.mappingsEmpty")}</Text>
        ) : (
          <Table withTableBorder withColumnBorders>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t("connections.labelMapping.colLabel")}</Table.Th>
                <Table.Th>{t("connections.labelMapping.colRole")}</Table.Th>
                <Table.Th>{t("connections.labelMapping.colValue")}</Table.Th>
                <Table.Th>{t("connections.colAction")}</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {mappingsQuery.data.mappings.map((row) => (
                <Table.Tr key={`${row.label_name}:${row.role}`}>
                  <Table.Td>{row.label_name}</Table.Td>
                  <Table.Td>{row.role}</Table.Td>
                  <Table.Td>
                    {row.role_value ?? (
                      <Text c="dimmed" size="sm">
                        —
                      </Text>
                    )}
                  </Table.Td>
                  <Table.Td>
                    <ActionIcon
                      color="red"
                      variant="subtle"
                      aria-label={t("connections.labelMapping.deleteButton")}
                      onClick={() => {
                        setDeleteTarget({
                          labelName: row.label_name,
                          role: row.role,
                        });
                      }}
                    >
                      ×
                    </ActionIcon>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}

        <Title order={4}>{t("connections.labelMapping.addTitle")}</Title>

        {rolesQuery.isError ? (
          <Alert color="red" title={t("connections.errorHeading")}>
            <Group justify="space-between">
              {t("connections.labelMapping.rolesError")}
              <Button
                size="xs"
                variant="light"
                loading={rolesQuery.isFetching}
                onClick={() => {
                  void rolesQuery.refetch();
                }}
              >
                {t("connections.labelMapping.rolesRetry")}
              </Button>
            </Group>
          </Alert>
        ) : null}

        {formError !== null ? (
          <Alert color="red" title={t("connections.errorHeading")}>
            {formError}
          </Alert>
        ) : null}

        <Group align="flex-end" gap="sm">
          <TextInput
            label={t("connections.labelMapping.labelNameLabel")}
            placeholder={t("connections.labelMapping.labelNamePlaceholder")}
            value={labelName}
            onChange={(event) => {
              setLabelName(event.currentTarget.value);
            }}
          />
          <Select
            label={t("connections.labelMapping.roleLabel")}
            data={roleOptions}
            disabled={rolesUnavailable}
            value={role}
            onChange={(value) => {
              setRole(value);
            }}
            placeholder={t("connections.labelMapping.rolePlaceholder")}
          />
          <TextInput
            label={t("connections.labelMapping.roleValueLabel")}
            placeholder={t("connections.labelMapping.roleValuePlaceholder")}
            value={roleValue}
            disabled={!requiresValue}
            onChange={(event) => {
              setRoleValue(event.currentTarget.value);
            }}
          />
          <Button
            onClick={handleAdd}
            loading={createMutation.isPending}
            disabled={rolesUnavailable}
          >
            {t("connections.labelMapping.addButton")}
          </Button>
        </Group>
      </Stack>

      {deleteTarget !== null ? (
        <DeleteMappingModal
          target={deleteTarget}
          pending={deleteMutation.isPending}
          onCancel={() => {
            setDeleteTarget(null);
          }}
          onConfirm={() => {
            deleteMutation.mutate({
              repository_full_name: repo,
              label_name: deleteTarget.labelName,
              role: deleteTarget.role,
            });
          }}
        />
      ) : null}
    </>
  );
}

// Цель удаления — строка маппинга, адресуемая парой «метка + роль» в scope.
interface DeleteTarget {
  readonly labelName: string;
  readonly role: string;
}

interface DeleteMappingModalProps {
  readonly target: DeleteTarget;
  readonly pending: boolean;
  readonly onCancel: () => void;
  readonly onConfirm: () => void;
}

// Confirm-модалка удаления соответствия. Рендерится только при наличии
// цели (родитель гейтит на deleteTarget !== null) — `target` гарантированно
// не null, лишнего guard'а в обработчике нет.
function DeleteMappingModal(props: DeleteMappingModalProps): ReactElement {
  const { t } = useTranslation();
  const { target, pending, onCancel, onConfirm } = props;
  return (
    <Modal
      opened
      onClose={onCancel}
      title={t("connections.labelMapping.deleteConfirmTitle")}
    >
      <Stack gap="md">
        <Text>
          {t("connections.labelMapping.deleteConfirmText", {
            label: target.labelName,
            role: target.role,
          })}
        </Text>
        <Group justify="flex-end">
          <Button variant="default" onClick={onCancel}>
            {t("connections.modalCancel")}
          </Button>
          <Button color="red" loading={pending} onClick={onConfirm}>
            {t("connections.labelMapping.deleteButton")}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
